import { localDateStr } from "@/lib/dateUtils";

/**
 * Today as YYYY-MM-DD in the account timezone (UTC when missing or invalid,
 * like the server). Shared by the card's midnight check and the offline
 * queue, so a tap is dated when it happens rather than when the card loaded.
 */
export function todayInZone(timeZone: string | null | undefined, now = new Date()): string {
  const tz = timeZone || "UTC";
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  }
}

/** The phone's own date and UTC offset, for dating a Pilot tap (Done, Rest,
 *  Undo, two minutes). The server takes the phone's date as "today" when it
 *  is believable — a traveller or a stale account zone no longer finds their
 *  day over at 8:30pm — and uses the offset to refuse an offline replay of a
 *  tap from a day that has since ended (backdating stays blocked). Offset is
 *  getTimezoneOffset(): minutes BEHIND UTC. */
export function phoneDay(now = new Date()): { localDate: string; tzOffset: number } {
  return { localDate: localDateStr(now), tzOffset: now.getTimezoneOffset() };
}

/** The current hour (0–23) in the account's timezone — the same clock the
 *  server uses for the coach's greeting. Falls back to the device clock. */
export function hourInZone(tz: string | null | undefined): number {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz || undefined, hour: "numeric", hour12: false }).formatToParts(new Date());
    const h = Number(parts.find((p) => p.type === "hour")?.value);
    return Number.isFinite(h) ? h % 24 : new Date().getHours();
  } catch {
    return new Date().getHours();
  }
}

/** Minutes since midnight in the account's timezone (device zone if none). */
export function minutesInZone(tz: string | null | undefined, now = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz || undefined, hour: "numeric", minute: "numeric", hour12: false }).formatToParts(now);
    const h = Number(parts.find((p) => p.type === "hour")?.value) % 24;
    const m = Number(parts.find((p) => p.type === "minute")?.value);
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
  } catch { /* bad zone → device clock */ }
  return now.getHours() * 60 + now.getMinutes();
}

/** The device's own IANA zone, or null on an old WebView. */
export function deviceZone(): string | null {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
}

/** How far `tz` is ahead of UTC at the instant `at`, in minutes. */
function zoneOffsetMinutes(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/** "2026-10-09" + "17:30" on the clock in `tz` → that instant. The device's
 *  clock when `tz` is missing or unknown; null for an unreadable date/time. */
export function zonedTimeToUtc(date: string, time: string, tz: string | null | undefined): Date | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!dm || !tm) return null;
  const [y, mo, d, h, mi] = [Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2])];
  if (h > 23 || mi > 59) return null;
  const device = () => new Date(y, mo, d, h, mi);
  if (!tz) return device();
  try {
    const wall = Date.UTC(y, mo, d, h, mi);
    // The zone's offset the day before and the day after this wall time (a
    // zone changes offset at most once in two days). Read the way the server
    // reads a naive time (Python's fold=0): the offset in force before a
    // change wins — the first 01:30 of an autumn night, and a spring-forward
    // 02:30 that never happens lands where the server puts it (03:30), not
    // an hour early (review 2026-10-09).
    const before = zoneOffsetMinutes(tz, new Date(wall - 86_400_000));
    const after = zoneOffsetMinutes(tz, new Date(wall + 86_400_000));
    const fits = (off: number) => zoneOffsetMinutes(tz, new Date(wall - off * 60_000)) === off;
    const off = fits(before) ? before : fits(after) ? after : before;
    return new Date(wall - off * 60_000);
  } catch {
    return device();
  }
}

/** "2026-10-09" plus n days (calendar arithmetic, no clock involved). */
export function addDaysTo(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

